import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { formatEntryId, parseEntryId } from "./entryId";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	plural,
	refreshBoth,
} from "./types";

/**
 * Per-entry export outcome, keyed by numeric id (the cache's row id type).
 * Mirrors the legacy `useExportEntries` result so callers that track
 * per-row export state (`ActionQueueProvider`'s `failedExports` map) keep
 * working after the migration to the v2 bulk endpoint.
 */
export type ExportResult = {
	exportedIds: number[];
	failedEntries: { id: number; error: string }[];
};

/** Wire shape of a per-entry export rejection (`id` is `ent_<n>`). */
type WireFailedEntry = { id: string; error: string };

/**
 * Thrown when the export landed `200` but some entries were rejected by the
 * billing-system validation / hash dedup. Carries the per-entry reasons so the
 * toast can show exactly what failed. Distinct from `ApiError` (a hard 4xx/5xx
 * that exported nothing — e.g. a deleted or already-exported id in the batch).
 */
class PartialExportError extends Error {
	readonly failedEntries: { id: number; error: string }[];
	constructor(failedEntries: { id: number; error: string }[], total: number) {
		const n = failedEntries.length;
		super(`${n} of ${total} ${plural(total)} failed to export`);
		this.name = "PartialExportError";
		this.failedEntries = failedEntries;
	}
}

/**
 * Run the bulk export and fan the outcome out to the optional callbacks.
 *
 *   POST /v2/entries/export { ids: ["ent_<n>", …] }
 *
 * Replaces the old per-row `/records/exportRecords` loop (`useExportEntries`)
 * — the server now flips every tracked/reviewed row to the terminal
 * `exported` state in one call, sharing the exact export pipeline the v1 path
 * used (firm pre-validation, hash dedup, firm-specific INSERT / SurePoint).
 *
 * Failure handling:
 *  - Whole-batch rejection (4xx/5xx: a deleted / already-exported / missing id,
 *    or a firm pre-validation failure) → throw `ApiError`/`Error`. There's no
 *    per-entry breakdown, so every id is attributed the failure for callers
 *    tracking per-row state.
 *  - Partial success (HTTP 200 with a non-empty `failedEntries`) → the rows in
 *    `exportedIds` are committed; the rest are reported via `onEntryFailure` /
 *    `onComplete`, then we throw `PartialExportError` so the action's error
 *    toast surfaces the reasons.
 *
 * `_actionId` is accepted (and ignored) so the legacy call site in
 * `ActionQueueProvider.editThenExport` keeps its signature.
 */
export async function executeExport(
	_actionId: string,
	ids: number[],
	deps: ActionDeps,
	onComplete?: (result: ExportResult) => void,
	onEntryFailure?: (id: number, error: string) => void,
	timekeeperId?: string | null,
): Promise<void> {
	const wireIds = ids.map(formatEntryId);

	const resp = await deps.makeApiRequest({
		endpoint: "/v2/entries/export",
		method: "POST",
		body: { ids: wireIds },
		headers: actingHeaders(timekeeperId),
	});

	if (resp.status === "ERROR") {
		const err = resp.apiError
			? new ApiError(resp.apiError)
			: new Error(resp.error || "Failed to export entries");
		for (const id of ids) onEntryFailure?.(id, err.message);
		onComplete?.({
			exportedIds: [],
			failedEntries: ids.map((id) => ({ id, error: err.message })),
		});
		throw err;
	}

	const data = resp.data ?? {};
	const exportedIds: number[] = (data.exportedIds ?? []).map(parseEntryId);
	const failedEntries = ((data.failedEntries ?? []) as WireFailedEntry[]).map(
		(f) => ({ id: parseEntryId(f.id), error: f.error }),
	);

	for (const f of failedEntries) onEntryFailure?.(f.id, f.error);
	onComplete?.({ exportedIds, failedEntries });

	if (failedEntries.length > 0) {
		throw new PartialExportError(failedEntries, ids.length);
	}
}

/**
 * Bulk export as a queued action.
 *
 * No optimistic update: an exported row transitions from mutable → immutable,
 * so a wrong optimistic flip (a row that fails billing validation server-side)
 * is worse than the brief flicker until `refreshBoth` reconciles. We read the
 * real state from the post-action refresh instead.
 *
 * `onComplete` / `onEntryFailure` are optional hooks for hosts that track
 * per-row export state outside the toast (e.g. the v2 Entries page's
 * `failedExports` map). The `/entries` page calls the two-arg form.
 */
export function exportAction(
	ids: number[],
	deps: ActionDeps,
	onComplete?: (result: ExportResult) => void,
	onEntryFailure?: (id: number, error: string) => void,
	timekeeperId?: string | null,
): QueuedAction {
	const n = ids.length;

	return createAction({
		execute: () =>
			executeExport("", ids, deps, onComplete, onEntryFailure, timekeeperId),
		rollback: null,
		toast: {
			loading: `Exporting ${n} ${plural(n)}…`,
			success: `Exported ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Some entries are already exported";
					if (err.code === "ENTRY_DELETED")
						return "Deleted entries can't be exported";
					if (err.code === "ENTRY_NOT_FOUND")
						return "One or more entries no longer exist";
				}
				if (err instanceof PartialExportError) {
					const { failedEntries } = err;
					if (failedEntries.length === 1) return failedEntries[0].error;
					const uniqueErrors = [...new Set(failedEntries.map((f) => f.error))];
					return `${failedEntries.length} entries failed: ${uniqueErrors.join("; ")}`;
				}
				return "Failed to export entries";
			},
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
